// /admin/analytics-events — telemetry events explorer.
//
// Flat-route companion to /admin/analytics. Filterable + paginated row table
// over `GET /api/admin/analytics/telemetry/events`. Each row exposes a
// "View props" modal for the parsed JSON payload.
//
// Why a flat sibling instead of `admin/analytics/events`: converting the
// existing flat `admin/analytics.tsx` to `admin/analytics/index.tsx` is a
// parent-owned change (route-tree restructure). A peer file at
// `admin/analytics-events.tsx` keeps the URL clean (`/admin/analytics-events`)
// without forcing that integration cost.
//
// Telemetry note: this surface intentionally does NOT call `track()`. We don't
// telemeter the telemetry UI — every filter change would recursively emit a
// row visible in the same explorer.

import {
  Anchor,
  Badge,
  Button,
  Code,
  Group,
  Loader,
  Modal,
  MultiSelect,
  Pagination,
  Paper,
  SegmentedControl,
  SimpleGrid,
  Stack,
  Table,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useDisclosure } from '@mantine/hooks';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { TelemetryDisplay, TelemetryEvents } from '@wwaave/shared';
import { useMemo, useState } from 'react';

import { AdminShell } from '../../components/admin-shell/AdminShell';
import { EmptyState, MotionSection, PageHero, SectionTitle } from '../../components/ui';
import { fetchOk } from '../../lib/api';

const { TELEMETRY_KINDS, TELEMETRY_SURFACES } = TelemetryEvents;
const { getTelemetryDisplay } = TelemetryDisplay;

const PAGE_SIZE = 50;

export const Route = createFileRoute('/admin/analytics-events')({
  component: AdminAnalyticsEventsPage,
});

type EventRow = {
  id: number;
  ts: number;
  ingestedAt: number;
  userId: string;
  sessionId: string;
  activeHat: string | null;
  kind: string;
  surface: string;
  pagePath: string | null;
  props: unknown;
  durationMs: number | null;
  userAgentClass: string | null;
  source: string;
};

type EventsResponse = {
  total: number;
  rows: EventRow[];
};

type SourceFilter = 'any' | 'client' | 'server';

interface Filters {
  kind: string[];
  surface: string[];
  userId: string;
  source: SourceFilter;
  from: Date | null;
  to: Date | null;
}

const INITIAL_FILTERS: Filters = {
  kind: [],
  surface: [],
  userId: '',
  source: 'any',
  from: null,
  to: null,
};

function buildQueryString(filters: Filters, page: number): string {
  const params = new URLSearchParams();
  for (const k of filters.kind) params.append('kind', k);
  for (const s of filters.surface) params.append('surface', s);
  if (filters.userId.trim().length > 0) params.set('userId', filters.userId.trim());
  if (filters.source !== 'any') params.set('source', filters.source);
  if (filters.from) params.set('from', String(startOfDayMs(filters.from)));
  if (filters.to) params.set('to', String(endOfDayMs(filters.to)));
  params.set('limit', String(PAGE_SIZE));
  params.set('offset', String((page - 1) * PAGE_SIZE));
  return params.toString();
}

function startOfDayMs(d: Date): number {
  const copy = new Date(d);
  copy.setHours(0, 0, 0, 0);
  return copy.getTime();
}

function endOfDayMs(d: Date): number {
  const copy = new Date(d);
  copy.setHours(23, 59, 59, 999);
  return copy.getTime();
}

const kindOptions = TELEMETRY_KINDS.map((kind) => ({
  value: kind,
  label: getTelemetryDisplay(kind).label,
}));

const surfaceOptions = TELEMETRY_SURFACES.map((surface) => ({
  value: surface,
  label: surface,
}));

function AdminAnalyticsEventsPage() {
  const [filters, setFilters] = useState<Filters>(INITIAL_FILTERS);
  const [page, setPage] = useState(1);
  const [selectedRow, setSelectedRow] = useState<EventRow | null>(null);
  const [modalOpened, modalHandlers] = useDisclosure(false);

  const queryString = buildQueryString(filters, page);

  const query = useQuery<EventsResponse>({
    queryKey: ['admin', 'telemetry-events', queryString],
    queryFn: async () => {
      const res = await fetchOk(`/api/admin/analytics/telemetry/events?${queryString}`);
      return (await res.json()) as EventsResponse;
    },
  });

  const totalPages = useMemo(() => {
    const total = query.data?.total ?? 0;
    return Math.max(1, Math.ceil(total / PAGE_SIZE));
  }, [query.data?.total]);

  const updateFilter = <K extends keyof Filters>(key: K, value: Filters[K]) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const resetFilters = () => {
    setFilters(INITIAL_FILTERS);
    setPage(1);
  };

  const openProps = (row: EventRow) => {
    setSelectedRow(row);
    modalHandlers.open();
  };

  const rows = query.data?.rows ?? [];
  const total = query.data?.total ?? 0;
  const hasResults = rows.length > 0;
  const anyFilter =
    filters.kind.length > 0 ||
    filters.surface.length > 0 ||
    filters.userId.trim().length > 0 ||
    filters.source !== 'any' ||
    filters.from !== null ||
    filters.to !== null;

  return (
    <AdminShell>
      <Stack gap="xl">
        <PageHero
          eyebrow="Admin · Analytics"
          title="Events explorer"
          lede="Raw telemetry rows from the last 90 days. Filter by kind, surface, user, or date range."
        />

        <MotionSection>
          <Paper withBorder radius="md" p="md">
            <Stack gap="md">
              <SectionTitle
                title="Filters"
                caption="Dropdown changes apply immediately. Text inputs apply on blur or Enter."
                action={
                  anyFilter ? (
                    <Anchor component="button" type="button" size="sm" onClick={resetFilters}>
                      Reset filters
                    </Anchor>
                  ) : null
                }
              />
              <Group gap="md" wrap="wrap" align="flex-end">
                <MultiSelect
                  label="Kind"
                  placeholder="Any kind"
                  data={kindOptions}
                  value={filters.kind}
                  onChange={(v) => updateFilter('kind', v)}
                  searchable
                  clearable
                  maxValues={5}
                  style={{ minWidth: 260, flex: 1 }}
                />
                <MultiSelect
                  label="Surface"
                  placeholder="Any surface"
                  data={surfaceOptions}
                  value={filters.surface}
                  onChange={(v) => updateFilter('surface', v)}
                  searchable
                  clearable
                  maxValues={5}
                  style={{ minWidth: 220, flex: 1 }}
                />
                <TextInput
                  label="User id"
                  placeholder="Exact match"
                  value={filters.userId}
                  onChange={(e) => updateFilter('userId', e.currentTarget.value)}
                  style={{ minWidth: 220, flex: 1 }}
                />
                <SegmentedControl
                  data={[
                    { label: 'Any source', value: 'any' },
                    { label: 'Client', value: 'client' },
                    { label: 'Server', value: 'server' },
                  ]}
                  value={filters.source}
                  onChange={(v) => updateFilter('source', v as SourceFilter)}
                  size="sm"
                />
                <DatePickerInput
                  label="From"
                  placeholder="Start date"
                  value={filters.from}
                  onChange={(v) => updateFilter('from', toDateOrNull(v))}
                  clearable
                  style={{ minWidth: 160 }}
                />
                <DatePickerInput
                  label="To"
                  placeholder="End date"
                  value={filters.to}
                  onChange={(v) => updateFilter('to', toDateOrNull(v))}
                  clearable
                  style={{ minWidth: 160 }}
                />
              </Group>
            </Stack>
          </Paper>
        </MotionSection>

        <MotionSection delay={0.05}>
          <Stack gap="md">
            <SectionTitle
              title={`${total.toLocaleString()} event${total === 1 ? '' : 's'}`}
              caption={
                query.isLoading
                  ? 'Loading…'
                  : hasResults
                    ? `Showing ${rows.length.toLocaleString()} of ${total.toLocaleString()} (newest first).`
                    : 'No rows match the current filters.'
              }
            />

            {query.isLoading ? (
              <Group>
                <Loader size="sm" />
                <Text c="dimmed" size="sm">
                  Fetching events…
                </Text>
              </Group>
            ) : query.isError ? (
              <EmptyState
                title="Events couldn't load"
                body="The telemetry endpoint returned an error. Try refreshing or loosening the filters."
                action={
                  <Button variant="light" color="brand" onClick={() => query.refetch()}>
                    Retry
                  </Button>
                }
              />
            ) : !hasResults ? (
              <EmptyState
                title="No events match these filters"
                body="Loosen the date range or pick a different kind to widen the result set."
                action={
                  anyFilter ? (
                    <Button variant="light" color="brand" onClick={resetFilters}>
                      Reset filters
                    </Button>
                  ) : null
                }
              />
            ) : (
              <Paper withBorder radius="md" style={{ overflow: 'hidden' }}>
                <Table striped highlightOnHover horizontalSpacing="md" verticalSpacing="sm">
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Time</Table.Th>
                      <Table.Th>Kind</Table.Th>
                      <Table.Th>Surface</Table.Th>
                      <Table.Th>User</Table.Th>
                      <Table.Th>Hat</Table.Th>
                      <Table.Th>Page</Table.Th>
                      <Table.Th>Source</Table.Th>
                      <Table.Th style={{ width: 110 }}>Props</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {rows.map((row) => (
                      <EventTableRow key={row.id} row={row} onOpenProps={() => openProps(row)} />
                    ))}
                  </Table.Tbody>
                </Table>
              </Paper>
            )}

            {hasResults && totalPages > 1 ? (
              <Group justify="space-between" wrap="wrap">
                <Text size="xs" c="dimmed">
                  Page {page} of {totalPages.toLocaleString()}
                </Text>
                <Pagination
                  value={page}
                  onChange={setPage}
                  total={totalPages}
                  withEdges
                  siblings={1}
                  color="brand"
                />
              </Group>
            ) : null}
          </Stack>
        </MotionSection>
      </Stack>

      <PropsModal opened={modalOpened} onClose={modalHandlers.close} row={selectedRow} />
    </AdminShell>
  );
}

function EventTableRow({ row, onOpenProps }: { row: EventRow; onOpenProps: () => void }) {
  const display = getTelemetryDisplay(row.kind);
  const sourceColor = row.source === 'client' ? 'brand' : row.source === 'server' ? 'tide' : 'gray';
  return (
    <Table.Tr>
      <Table.Td>
        <Tooltip label={new Date(row.ts).toISOString()} withArrow>
          <Text size="xs">{formatTimestamp(row.ts)}</Text>
        </Tooltip>
      </Table.Td>
      <Table.Td>
        <Stack gap={2}>
          <Text size="sm" fw={500}>
            {display.label}
          </Text>
          <Code style={{ fontSize: 10 }}>{row.kind}</Code>
        </Stack>
      </Table.Td>
      <Table.Td>
        <Text size="xs">{row.surface}</Text>
      </Table.Td>
      <Table.Td>
        <Tooltip label={row.userId} withArrow disabled={row.userId.length === 0}>
          <Text size="xs" ff="monospace">
            {row.userId.length > 0 ? `${row.userId.slice(0, 8)}…` : '–'}
          </Text>
        </Tooltip>
      </Table.Td>
      <Table.Td>
        {row.activeHat === null ? (
          <Text size="xs" c="dimmed">
            –
          </Text>
        ) : (
          <Text size="xs">{row.activeHat}</Text>
        )}
      </Table.Td>
      <Table.Td>
        <Tooltip label={row.pagePath ?? ''} withArrow disabled={!row.pagePath}>
          <Text size="xs" style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {row.pagePath ?? '–'}
          </Text>
        </Tooltip>
      </Table.Td>
      <Table.Td>
        <Badge variant="light" color={sourceColor} size="xs" tt="lowercase">
          {row.source}
        </Badge>
      </Table.Td>
      <Table.Td>
        <Button variant="subtle" size="xs" onClick={onOpenProps}>
          View props
        </Button>
      </Table.Td>
    </Table.Tr>
  );
}

function PropsModal({
  opened,
  onClose,
  row,
}: {
  opened: boolean;
  onClose: () => void;
  row: EventRow | null;
}) {
  const propsJson = useMemo(() => {
    if (!row) return '';
    if (row.props === null || row.props === undefined) return '(no props)';
    try {
      return JSON.stringify(row.props, null, 2);
    } catch {
      return String(row.props);
    }
  }, [row]);

  const display = row ? getTelemetryDisplay(row.kind) : null;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size="lg"
      title={
        row && display ? (
          <Stack gap={2}>
            <Text fw={600}>
              Event #{row.id} · {display.label}
            </Text>
            <Code style={{ fontSize: 11 }}>{row.kind}</Code>
          </Stack>
        ) : (
          'Event'
        )
      }
    >
      {row ? (
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            {new Date(row.ts).toLocaleString()}
          </Text>

          <Stack gap={4}>
            <Text size="xs" fw={600} tt="uppercase" c="dimmed">
              Props
            </Text>
            <Code
              block
              style={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontSize: 12,
                lineHeight: 1.5,
                maxHeight: 360,
                overflow: 'auto',
              }}
            >
              {propsJson}
            </Code>
          </Stack>

          <Stack gap={4}>
            <Text size="xs" fw={600} tt="uppercase" c="dimmed">
              Context
            </Text>
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs">
              <MetaItem label="Session" value={row.sessionId} mono />
              <MetaItem label="User" value={row.userId} mono />
              <MetaItem label="Surface" value={row.surface} />
              <MetaItem label="Active hat" value={row.activeHat ?? '–'} />
              <MetaItem label="Page" value={row.pagePath ?? '–'} />
              <MetaItem label="Source" value={row.source} />
              <MetaItem
                label="Duration"
                value={row.durationMs === null ? '–' : `${row.durationMs} ms`}
              />
              <MetaItem label="UA class" value={row.userAgentClass ?? '–'} />
              <MetaItem
                label="Ingested"
                value={new Date(row.ingestedAt).toLocaleString()}
              />
            </SimpleGrid>
          </Stack>
        </Stack>
      ) : null}
    </Modal>
  );
}

function MetaItem({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <Group gap={6} wrap="nowrap">
      <Text size="xs" c="dimmed" style={{ minWidth: 80 }}>
        {label}:
      </Text>
      {mono ? (
        <Text size="xs" ff="monospace" style={{ wordBreak: 'break-all' }}>
          {value}
        </Text>
      ) : (
        <Text size="xs" style={{ wordBreak: 'break-all' }}>
          {value}
        </Text>
      )}
    </Group>
  );
}

/**
 * Mantine v7's DatePickerInput typings allow `string | Date | null` depending
 * on `valueFormat`. We always want a JS Date, so normalise here.
 */
function toDateOrNull(value: string | Date | null): Date | null {
  if (value === null) return null;
  if (value instanceof Date) return value;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatTimestamp(ts: number): string {
  const d = new Date(ts);
  const date = d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
  const time = d.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  return `${date} · ${time}`;
}
